import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation, Link } from "react-router-dom";
import { useServices } from "../../context/ServiceProvider";
import { pushBreadcrumb } from "../../store/slices/navigationSlice";
import { Alert, Spin, Tabs, Typography, Button } from "antd";
import {
  renderGrid,
  setByPathCloned,
  buildMinimalPatch,
} from "../common/FieldRenderer";
import PreviewOrchestrator from "../preview/PreviewOrchestrator";
import { useRecents } from "../../hooks/useRecents";
import { EyeOff, Eye, Edit, Zap, Save } from "lucide-react";
import { validateProjectQualStepEditForm } from "../../config/projectQualStepEdit.validation";
import {
  PQS_PICKLIST_LABELS,
  buildInfoFieldsForPqs,
  buildLookupFieldsForPqs,
  buildSysFieldsForPqs,
} from "./projectQualStepColumnsConfig";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";
import { SOBJECT_PROJECT_QUAL_STEP } from "../../config/sobjectNames";

export default function ProjectQualStepDetails() {
  const { projectQualStep, http } = useServices();
  const { qualStepId } = useParams();
  const dispatch = useDispatch();
  const breadcrumbs = useSelector((state) => state.navigation.breadcrumbs);
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const { picklistMap } = usePicklistMap(
    Object.values(PQS_PICKLIST_LABELS),
    SOBJECT_PROJECT_QUAL_STEP,
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [previewWidth, setPreviewWidth] = useState(50); // Percentage of available width
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [hideValidationSuccess, setHideValidationSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [payload, setPayload] = useState(null);
  const containerRef = useRef(null);
  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const recordedIdRef = React.useRef(null);

  // Handle preview resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left - 20;
      const newWidth = Math.min(
        Math.max((mouseX / containerWidth) * 100, 20),
        66.67,
      ); // Max 2/3 (66.67%)

      setPreviewWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!qualStepId) return;
      setData(null);
      setLoading(true);
      setError("");
      try {
        const record = await projectQualStep.fetchQualStep(qualStepId);
        if (!ignore) setData(record);
      } catch (e) {
        if (!ignore)
          setError(e?.message || "Failed to load qualification step");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [qualStepId, projectQualStep]);

  useEffect(() => {
    if (!data && !form) return;
    updatePayload();
  }, [data, form?.info]);

  const updatePayload = () => {
    const record = form || data;
    console.log("updatePayload entered", data);
    setPayload({
      id: record?.id,
      name: record?.info?.name || "",
      projectId: record?.info?.projectId,
      projectObjectiveId: record?.info?.projectObjectiveId || "",
      status: "Current",
      duration: record?.info?.estimatedTimeMinutes || "",
      current_attempt: "1 of " + (record?.info?.numberOfAttempts || 1),
      result_date: record?.info?.result_date || "",
      document_title: record?.info?.document_title,
      document_content: record?.info?.document_content || "",
      qualification_step_url: record?.info?.qualification_step_url || "",
      qualification_type: record?.info?.qualification_type || "",
      deliveryToolName: record?.info?.deliveryToolName || "",
      deliveryToolAccount: record?.info?.deliveryToolAccount || "",
    });
  };

  // Push breadcrumb when PQS loads
  useEffect(() => {
    const id = data?.id || qualStepId;
    const provided = location?.state?.crumbLabel;
    const fallbackName = data?.info?.name;
    const name = provided || fallbackName;
    if (id && name) {
      if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
        dispatch(pushBreadcrumb({ label: "Home", path: "/", type: "home" }));
      }
      dispatch(
        pushBreadcrumb({
          id,
          label: name || `Project Qual Step ${id}`,
          path: `/pqs/${id}`,
          type: "projectQualStep",
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.id,
    data?.info?.name,
    qualStepId,
    location.pathname,
    breadcrumbs?.length,
    location?.state?.crumbLabel,
  ]);

  // Record in Recently Viewed when opened
  useEffect(() => {
    const id = data?.id || qualStepId;
    if (data == null || !id || recordedIdRef.current === id) return;
    const name = data?.info?.name || "Project Qual Step";
    record({
      id: String(id),
      title: String(name || id),
      url: `/pqs/${id}`,
      type: "Project Qual Step",
    });
    recordedIdRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, qualStepId]);

  const info = data?.info || {};
  const sys = data?.sys || {};

  useEffect(() => {
    if (isEditing) {
      setForm(data);
      setFormErrors({});
      setSuccess("");
    }
  }, [isEditing, data]);

  const loadPreview = async () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    setShowPreview(true);
    const pqsId = data?.id || qualStepId;
    if (!pqsId) return;
    setPreviewLoading(true);
    setPreviewError("");
    setPayload(null);
    try {
      const resp = await http.sfGet(`/ProjectQualStep`, {
        id: String(pqsId),
      });
    } catch (e) {
      setPreviewError(e?.message || "Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  function handleChange(path, value) {
    const next = setByPathCloned(form || {}, path, value);
    setForm(next);
    setFormErrors(validateProjectQualStepEditForm(next));
    setError("");
  }

  const validationGroups = useMemo(() => {
    const alerts = Array.isArray(validation?.alerts) ? validation.alerts : [];
    const groups = { error: [], warning: [], info: [] };
    for (const a of alerts) {
      const t = (a?.type === "warn" ? "warning" : a?.type) || "info";
      if (t === "error" || t === "warning" || t === "info") {
        groups[t].push(a);
      } else {
        groups.info.push(a);
      }
    }
    return groups;
  }, [validation]);

  async function handleValidate() {
    const id = data?.id || qualStepId;
    if (!id) {
      setValidationError("Missing record id");
      return;
    }
    setValidationError("");
    setValidating(true);
    setSuccess("");
    setHideValidationSuccess(false);
    try {
      const resp = await http.sfGet(`/Validate`, {
        id: String(id),
        type: "ProjectQualStep",
      });
      if (resp == null) {
        setValidation(null);
        return;
      }
      const successFlag = resp?.success === true;
      const alerts = Array.isArray(resp?.alerts) ? resp.alerts : [];
      // Local warnings to augment server validation
      const localAlerts = [];
      const recordForChecks = form || data;
      const attempts = Number(
        (recordForChecks?.info?.numberOfAttempts ?? "").toString(),
      );
      if (Number.isFinite(attempts) && attempts > 1) {
        localAlerts.push({
          id: "pqs-multi-attempts",
          type: "warning",
          message:
            "Number of Attempts is greater than 1. Most Qualification Steps allow only one attempt by default. Ensure the selected Qualification Step supports multiple attempts, or reduce this to 1 to avoid Salesforce errors.",
        });
      }
      setValidation({
        success: successFlag,
        alerts: [...alerts, ...localAlerts],
      });
    } catch (e) {
      const details = e?.details;
      if (details && typeof details === "object") {
        const alerts = Array.isArray(details.alerts) ? details.alerts : [];
        const localAlerts = [];
        const recordForChecks = form || data;
        const attempts = Number(
          (recordForChecks?.info?.numberOfAttempts ?? "").toString(),
        );
        if (Number.isFinite(attempts) && attempts > 1) {
          localAlerts.push({
            id: "pqs-multi-attempts",
            type: "warning",
            message:
              "Number of Attempts is greater than 1. Most Qualification Steps allow only one attempt by default. Ensure the selected Qualification Step supports multiple attempts, or reduce this to 1 to avoid Salesforce errors.",
          });
        }
        setValidation({
          success: !!details.success,
          alerts: [...alerts, ...localAlerts],
        });
      } else {
        setValidation(null);
        setValidationError(e?.message || "Validation failed");
      }
    } finally {
      setValidating(false);
    }
  }

  async function handleSave() {
    if (!form) return;
    const errs = validateProjectQualStepEditForm(form);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError("");
      return;
    }
    const id = data?.id || qualStepId;
    if (!id) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const partial = buildMinimalPatch(data || {}, form || {}) || {};
      if (Object.keys(partial).length === 0) {
        setIsEditing(false);
        setSuccess("No changes to update");
        return;
      }
      await projectQualStep.patchQualStep(String(id), partial);
      const fresh = await projectQualStep.fetchQualStep(String(id));
      setData(fresh);
      setIsEditing(false);
      setSuccess("Qualification Step updated");
    } catch (e) {
      setError(e?.message || "Failed to update qualification step");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Sticky Header with Title and Action Buttons */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 rounded-lg p-4 mb-6 shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <div className="flex items-center space-x-2">
                  <Typography.Title
                    level={5}
                    style={{ margin: 0, color: "#6b7280" }}
                  >
                    Project Qualification Step
                  </Typography.Title>
                </div>
                <Typography.Title level={2} style={{ margin: 0, marginTop: 4 }}>
                  {data?.info?.name || "Unnamed Step"}
                </Typography.Title>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isEditing ? (
                <Button
                  type="primary"
                  onClick={() => setIsEditing(true)}
                  icon={<Edit size={16} />}
                  className="shadow-sm"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      setForm(null);
                      setFormErrors({});
                    }}
                    className="shadow-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    disabled={saving || Object.keys(formErrors).length > 0}
                    loading={saving}
                    className="shadow-sm"
                    icon={<Save size={16} />}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}

              <div className="h-6 w-px bg-gray-300"></div>

              <Button
                type="default"
                onClick={handleValidate}
                loading={validating}
                className="shadow-sm"
              >
                {validating ? "Validating..." : "Validate"}
              </Button>

              <Button
                type={showPreview ? "primary" : "default"}
                onClick={() => {
                  loadPreview();
                }}
                icon={showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                className="shadow-sm"
              >
                {showPreview ? "Hide Preview" : "Show Preview"}
              </Button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mb-6">
            <Spin fullscreen tip="Loading qualification step…" />
          </div>
        )}
        {error && (
          <div className="mb-6">
            <Alert type="error" showIcon message={error} />
          </div>
        )}
        {validationError && (
          <div className="mb-6">
            <Alert type="error" showIcon message={validationError} />
          </div>
        )}
        {validation && (
          <div className="mb-6 space-y-4">
            {Array.isArray(validation.alerts) &&
            validation.alerts.length === 0 ? (
              !hideValidationSuccess ? (
                <div
                  className="fade-out-after-3s"
                  onAnimationEnd={() => setHideValidationSuccess(true)}
                >
                  <Alert
                    type="success"
                    showIcon
                    message="Validation Successful"
                    className="border-green-200 bg-green-50"
                  />
                </div>
              ) : null
            ) : (
              <div className="space-y-4">
                {validationGroups.error.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    message={`Validation Errors (${validationGroups.error.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.error.map((a, idx) => (
                          <li key={`err-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-red-200 bg-red-50"
                  />
                )}
                {validationGroups.warning.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={`Warnings (${validationGroups.warning.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.warning.map((a, idx) => (
                          <li key={`warn-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-yellow-200 bg-yellow-50"
                  />
                )}
                {validationGroups.info.length > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    message={`Information (${validationGroups.info.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.info.map((a, idx) => (
                          <li key={`info-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-blue-200 bg-blue-50"
                  />
                )}
              </div>
            )}
          </div>
        )}
        {success && (
          <div className="mb-6">
            <Alert type="success" showIcon message={success} />
          </div>
        )}

        {data && (
          <div
            ref={containerRef}
            className={`${showPreview ? "flex gap-4" : "max-w-6xl mx-auto"} ${
              isDragging ? "select-none cursor-col-resize" : ""
            }`}
          >
            <div
              className={`${
                showPreview ? `flex-shrink-0` : ""
              } h-min bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/20`}
              style={{
                width: showPreview ? `${previewWidth}%` : "100%",
              }}
            >
              <div className="p-6">
                <Tabs
                  defaultActiveKey="info"
                  items={[
                    {
                      key: "info",
                      label: "Info",
                      children: renderGrid(
                        resolvePicklistFields(
                          [
                            {
                              label: "Salesforce Id",
                              value: data?.id || "—",
                              readOnly: true,
                            },
                            ...buildInfoFieldsForPqs({
                              isEditing,
                              form,
                              data,
                            }),
                          ],
                          picklistMap,
                        ),
                        {
                          editing: isEditing,
                          form: form || data,
                          onChange: (p, v) => handleChange(p, v),
                          errors: formErrors,
                          onEnableEdit: () => setIsEditing(true),
                          originalData: data,
                          searchParams: { projectId: data?.info?.projectId },
                        },
                      ),
                    },
                    {
                      key: "sys",
                      label: "System",
                      children: renderGrid(buildSysFieldsForPqs(), {
                        editing: false,
                        form: form || data,
                        onEnableEdit: null,
                      }),
                    },
                  ]}
                />
              </div>
            </div>
            {showPreview && (
              <>
                {/* Drag Handle */}
                <div
                  className="w-2 cursor-col-resize flex-shrink-0 transition-colors duration-200 relative group"
                  onMouseDown={() => setIsDragging(true)}
                >
                  <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bg-gray-300 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-6 rounded-full group-hover:opacity-100 transition-opacity duration-200" />
                </div>
                <div
                  className="flex-shrink-0"
                  style={{ width: `calc(${100 - previewWidth}% - 40px)` }}
                >
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Live Preview
                      </h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <Zap size={16} className="mr-2" />
                        Real-time rendering
                      </div>
                    </div>
                    <div className="rounded-lg">
                      {previewLoading && (
                        <div className="mb-6">
                          <Spin size="small" tip="Loading preview…" />
                        </div>
                      )}
                      {previewError && (
                        <div className="mb-6">
                          <Alert type="error" showIcon message={previewError} />
                        </div>
                      )}
                      {!previewLoading && !previewError && payload && (
                        <PreviewOrchestrator
                          objectType="QualStep"
                          id={data?.id}
                          payload={payload}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
